<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";

import type { ExternalSystem, ApplicationCredentialNewData } from "@/types/externalToken";

import { authExternalStore } from "@/stores/authExternal";
import { addErrorBottom } from "@/stores/messages";

import { getErrorMessage } from "@/mixins/error";

import UCInput from "@/components/ui/UCInput.vue";
import UCSelect from "@/components/ui/UCSelect.vue";
import Modal from "@/components/elements/Modal.vue";
import ListApplicationCredentials from "@/components/elements/ListApplicationCredentials.vue";
import ErrorBox from "@/components/ui/ErrorBox.vue";

const authExternalStorage = authExternalStore();

const availableExternalSystems = ref<ExternalSystem[]>([]);
const oAuthExternalSystems = ref<ExternalSystem[]>([]);
const showAddModal = ref(false);
const name = ref('');
const clientID = ref('');
const clientSecret = ref('');
const creating = ref(false);
const selectedExternalSystem = ref({label: '', value: ''});
const errorMessage = ref('');
const fieldErrors = ref<any>({});

watch(showAddModal, (val) => {
  if (val && availableExternalSystemOptions.value && availableExternalSystemOptions.value.length === 1) {
    changeSelectedExternalSystem(availableExternalSystemOptions.value[0]);
  }
})

const availableExternalSystemOptions = computed(() => {
  return availableExternalSystems.value.map(s => ({label: s.name, value: s.system}));
});

async function createCredential() {
  if (selectedExternalSystem.value.value.length < 1) return;

  creating.value = true;
  const message = {
    name: name.value,
    token_id: clientID.value,
    token: clientSecret.value,
    token_type: "OAUTH2_APP"
  } as ApplicationCredentialNewData;

  try {
    await authExternalStorage.createNewApplicationCredential(message, selectedExternalSystem.value.value);
    fetchLists(true);
    closedModal();
  } catch (e: any) {
    if (e.response?.data?.code != "VALIDATION_ERROR" && e.response?.status != 400) {
      errorMessage.value = getErrorMessage(e)?.message ?? e.response?.data?.message;
    } else if (e.response?.data?.errors && e.response?.data?.errors.length > 0) {
      setFieldErrors(e.response?.data?.errors);
    } else {
      errorMessage.value = e.response?.data?.message ?? getErrorMessage(e)?.message;
    }
  }
  creating.value = false;
}

function changeSelectedExternalSystem(value: any) {
  selectedExternalSystem.value = value;

  if (selectedExternalSystem.value.label) {
    name.value = JSON.parse(JSON.stringify(selectedExternalSystem.value.label));
  }
}

function closedModal() {
  showAddModal.value = false;
  selectedExternalSystem.value = {label: '', value: ''};
  name.value = '';
  clientID.value = '';
  clientSecret.value = '';
  clearErrors();
}

function setFieldErrors(errors: any[]) {
  errors.forEach(e => {
    fieldErrors.value[e.field] = e.field_errors[0];
  });
}

function getFieldError(fieldId: string) {
  if (fieldId && fieldErrors.value[fieldId]?.message !== undefined) {
    return fieldErrors.value[fieldId].message ?? '';
  }

  return '';
}

function clearErrors() {
  if (errorMessage.value.length > 0) {
    errorMessage.value = '';
  }
  fieldErrors.value = {};
}

async function fetchOAuthSystemList(reload: boolean = false) {
  try {
    oAuthExternalSystems.value = await authExternalStorage.getOAuthExternals(reload);
  } catch (e: any) {
    addErrorBottom(e);
  }
}

async function fetchSystemList(reload: boolean = false) {
  try {
    availableExternalSystems.value = await authExternalStorage.getIntegrationExternalsWithoutCredentials(reload);
  } catch (e: any) {
    addErrorBottom(e);
  }
}

async function deleteCredential(id: string) {
  try {
    await authExternalStorage.deleteAccessTokens(id);
    fetchLists(true);
  } catch (e: any) {
    addErrorBottom(e);
  }
}

function fetchLists(reload: boolean = false) {
  fetchOAuthSystemList(reload);
  fetchSystemList(reload);
}

onMounted(() => {
  fetchLists();
});

</script>
<template>
  <div class="page-settings-section page-settings-section--application-credentials">
    <h1 class="page-settings-section__title">
      {{ $t('settings.application_credentials.name') }}
    </h1>
    <div class="page-settings-section__main">
      <button
        @click="showAddModal = true"
        class="button button--secondary">
        {{ $t('application_credentials.add.title') }}
      </button>

      <div class="page-settings-section__main__info">
        {{ $t('settings.application_credentials.description') }} <a href="https://support.unfoldedcircle.com/" target="_blank">{{ $t('application_credentials.add.learn_more') }}</a>
      </div>

      <ListApplicationCredentials 
        :credentials="oAuthExternalSystems"
        @delete="deleteCredential"
      />

      <Teleport to="body">
        <Modal
          :show="showAddModal"
          @close="closedModal"
          :name="'add-application-credential'"
          class="modal--add-application-credential"
        >
          <template #header>
            {{ $t('application_credentials.add.title') }}
          </template>
          <p>{{ $t('application_credentials.add.description') }} <a href="https://support.unfoldedcircle.com/" target="_blank">{{ $t('application_credentials.add.learn_more') }}</a></p>

          <div class="select-extra">
            <div class="select-extra__text">
              <span class="select-extra__label">
                {{ $t("application_credentials.add.integration") }}
              </span>
            </div>
            <UCSelect
              v-model="selectedExternalSystem"
              @select="changeSelectedExternalSystem"
              :options="availableExternalSystemOptions"
              :dynamic-width="true"
              :dynamic-position="true"
              :light="true"
            />
          </div>
          
          <UCInput
            v-model="name"
            @click="clearErrors"
            :label="$t('application_credentials.add.name')"
            :type="'text'"
            :disable-blur="true"
            :error-message="getFieldError('name')"
          />

          <UCInput
            v-model="clientID"
            @click="clearErrors"
            :label="$t('application_credentials.add.client_id.label')"
            :description="$t('application_credentials.add.client_id.description')"
            :type="'text'"
            :disable-blur="true"
            :error-message="getFieldError('token_id')"
          />

          <UCInput
            v-model="clientSecret"
            @click="clearErrors"
            :label="$t('application_credentials.add.client_secret.label')"
            :description="$t('application_credentials.add.client_secret.description')"
            :type="'password'"
            :disable-blur="true"
            :error-message="getFieldError('token')"
          />
          <ErrorBox v-if="errorMessage" :message="errorMessage" />

          <template #footer>
            <button
              @click="createCredential"
              :disabled="creating || name.length < 1 || clientID.length < 1 || clientSecret.length < 1 || errorMessage?.length > 0 || selectedExternalSystem?.value.length < 1"
              class="button button--primary button--min-w">
              {{ $t('ui.add') }}
            </button>
          </template>
        </Modal>
      </Teleport>
    </div>
  </div>
</template>